import React from 'react';
import PropTypes from 'prop-types';
import {
  FiArrowUp, FiArrowDown, FiClock,
} from 'react-icons/fi';
import dayjs from 'dayjs';

function StockMovementLog({ movements, className }) {
  // Define getMovementIcon first
  const getMovementIcon = (type) => {
    switch (type) {
      case 'in':
        return <FiArrowUp className="text-green-500" />;
      case 'out':
        return <FiArrowDown className="text-red-500" />;
      case 'adjustment':
        return <FiArrowUp className="text-blue-500" />;
      default:
        return <FiClock className="text-gray-500" />;
    }
  };

  // Sample mock data for restaurant inventory
  const mockMovements = [
    {
      id: 'mock-1',
      productName: 'Chicken Breast',
      productNameBn: 'চিকেন ব্রেস্ট',
      type: 'in',
      quantity: 20,
      unit: 'kg',
      pricePerUnit: 320,
      supplier: 'Meat Suppliers Ltd',
      timestamp: new Date(Date.now() - 86400000), // Yesterday
      user: 'Kitchen Manager',
      reason: 'Weekly restock',
      batchNo: 'CHK-2023-11-15',
    },
    {
      id: 'mock-2',
      productName: 'Organic Tomatoes',
      productNameBn: 'টমেটো',
      type: 'out',
      quantity: 3.5,
      unit: 'kg',
      pricePerUnit: 80,
      usedIn: 'Dinner service',
      timestamp: new Date(Date.now() - 3600000), // 1 hour ago
      user: 'Sous Chef',
      reason: 'Evening prep (3x Margherita, 2x Bolognese)',
      expiryDate: new Date(Date.now() + 86400000 * 3),
    },
    {
      id: 'mock-3',
      productName: 'Extra Virgin Olive Oil',
      productNameBn: 'অলিভ অয়েল',
      type: 'adjustment',
      quantity: 0.5,
      unit: 'liters',
      pricePerUnit: 1200,
      timestamp: new Date(Date.now() - 7200000), // 2 hours ago
      user: 'Inventory Staff',
      reason: 'Spillage during transfer',
      actionTaken: 'Logged incident, trained staff',
    },
    {
      id: 'mock-4',
      productName: 'Basmati Rice',
      productNameBn: 'বাসমতি চাল',
      type: 'in',
      quantity: 25,
      unit: 'kg',
      pricePerUnit: 95,
      supplier: 'Grain Distributors Inc',
      timestamp: new Date(Date.now() - 43200000), // 12 hours ago
      user: 'Store Manager',
      reason: 'Monthly bulk order',
      storageLocation: 'Dry Store A',
    },
  ];
  // Determine which data to display
  const displayMovements = (movements && Array.isArray(movements) && movements.length > 0)
    ? movements
    : mockMovements;
  const isMockData = !movements || !Array.isArray(movements) || movements.length === 0;

  return (
    <>
      <h2 className="px-6 py-4 text-xl font-semibold text-gray-800">
        Stock Movement Log
        {isMockData && (
          <span className="ml-2 text-sm font-normal text-gray-500">
            (Showing sample data)
          </span>
        )}
      </h2>
      <div className={`bg-white shadow rounded-lg overflow-hidden ${className}`}>
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Stock Movement History
            {isMockData && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                - Demo Preview
              </span>
            )}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-6 py-4">
            {displayMovements.map((movement) => (
              <div key={movement.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3 mt-1">
                    {getMovementIcon(movement.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {movement.productName}
                          {movement.productNameBn && (
                          <span className="text-gray-500 ml-2">
                            (
                            {movement.productNameBn}
                            )
                          </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {dayjs(movement.timestamp).format('DD MMM, h:mm A')}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        // eslint-disable-next-line no-nested-ternary
                        movement.type === 'in'
                          ? 'bg-green-100 text-green-800'
                          : movement.type === 'out'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                      }`}
                      >
                        {movement.type.toUpperCase()}
                      </span>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-gray-500">Quantity</p>
                        <p>
                          {movement.quantity}
                          {' '}
                          {movement.unit}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Price</p>
                        <p>
                          ৳
                          {(movement.pricePerUnit || 0).toFixed(2)}
                          /
                          {movement.unit}
                        </p>
                      </div>
                      {movement.supplier && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Supplier</p>
                        <p>{movement.supplier}</p>
                      </div>
                      )}
                      {movement.usedIn && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Used in</p>
                        <p>{movement.usedIn}</p>
                      </div>
                      )}
                    </div>

                    {movement.reason && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-sm text-gray-500">Notes</p>
                      <p className="text-sm">{movement.reason}</p>
                    </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {isMockData && (
            <div className="px-6 py-4 text-center bg-gray-50">
              <p className="text-sm text-gray-500">
                This is sample data. Your actual inventory movements will appear here.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

StockMovementLog.propTypes = {
  movements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      productName: PropTypes.string.isRequired,
      productNameBn: PropTypes.string,
      type: PropTypes.oneOf(['in', 'out', 'adjustment']).isRequired,
      quantity: PropTypes.number.isRequired,
      unit: PropTypes.string.isRequired,
      timestamp: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]).isRequired,
      user: PropTypes.string,
      reason: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
};

StockMovementLog.defaultProps = {
  movements: [],
  className: '',
};

export default StockMovementLog;
